// A message hit's view and link for both top-search surfaces, peers resolved from the roster.
import type { Message } from "$lib/core/types.js";
import { agentDisplayName } from "$lib/agent-display.js";
import { memberEmailLocalPart } from "$lib/member-email";
import {
  classifySearchResult,
  type SearchResultChannel,
  type SearchResultView,
} from "$lib/message-search-result.js";
import { authState, cyboState, workspaceState } from "$lib/state/app.svelte.js";
import { sessionDisplayName } from "$lib/state/session-alias.svelte.js";

function isAgentPeer(id: string): boolean {
  return (
    cyboState.list.some((c) => c.id === id) ||
    workspaceState.agents.some((a) => a.agentId === id || a.cyboId === id)
  );
}

// A DM counterpart's name: member, cybo, agent session, then the auth cache that also knows
// guests/externals (the DM header's chain). Shared by search, Saved and Threads.
export function messagePeerName(id: string): string | undefined {
  if (id === authState.user?.id) return authState.user?.name ?? "You";
  const member = workspaceState.members.find((m) => m.userId === id);
  if (member) return member.name ?? (memberEmailLocalPart(member) || undefined);
  const cybo = cyboState.list.find((c) => c.id === id);
  if (cybo) return cybo.name;
  const agent = workspaceState.agents.find((a) => a.agentId === id || a.cyboId === id);
  if (agent)
    return agent.agentId === id
      ? sessionDisplayName(agent, cyboState.list)
      : agentDisplayName(agent, cyboState.list);
  return authState.getMemberName(id) ?? undefined;
}

function isHumanMember(id: string): boolean {
  return workspaceState.members.some((m) => m.userId === id);
}

export function messageResultView(
  m: Message,
  wsId: string | null,
  getChannel: (id: string) => SearchResultChannel | undefined,
): SearchResultView {
  return classifySearchResult(m, {
    workspaceId: m.workspaceId ?? wsId ?? undefined,
    // The user OBJECT, not `.id` — `isMe` needs the whole canonical-id set.
    viewer: authState.user,
    getChannel,
    getPeerName: messagePeerName,
    isAgentPeer,
    isHumanMember,
  });
}
